import type { Breakpoint } from '@ctw/config/zui';
import { Button } from '@ctw/shared/components/button';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import type { UseScrollSpyResult } from '@ctw/shared/hooks/use-scroll-spy';
import {
  TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_ITEM_TEST_ID,
  TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_MENU_BUTTON_TEST_ID,
  TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_MENU_ITEM_TEST_ID,
  TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_MENU_TEST_ID,
  TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_TEST_ID,
  TABLE_OF_CONTENTS_SCROLL_SPY_VERTICAL_ITEM_TEST_ID,
  TABLE_OF_CONTENTS_SCROLL_SPY_VERTICAL_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { faList } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { debounce } from 'lodash-es';
import { Fragment, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface TableOfContentsItem {
  title: string;
  urlParameterId: string;
  contentDomId: string;
  selected?: boolean;
}

interface TableOfContentsScrollSpyContentBreakpoints {
  collapseToTabsAtOrBelow: Breakpoint;
}

const defaultBreakpoints: TableOfContentsScrollSpyContentBreakpoints = {
  collapseToTabsAtOrBelow: 'sm',
};

export interface TableOfContentsScrollSpyProps {
  title: string;
  scrollSpy: UseScrollSpyResult['scrollSpy'];
  items: Array<TableOfContentsItem>;
  containerRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  contentDomClass: string;
  contentBreakpoints?: TableOfContentsScrollSpyContentBreakpoints;
  urlParameterName: string;
  deepLinkItems?: boolean;
}

export const TableOfContentsScrollSpy = ({
  title,
  scrollSpy,
  items,
  containerRef,
  contentRef,
  contentDomClass,
  urlParameterName,
  contentBreakpoints = defaultBreakpoints,
  deepLinkItems = true,
}: TableOfContentsScrollSpyProps) => {
  const initialSearchParams = new URLSearchParams(window.location.search);
  const initialUrlParameterId = initialSearchParams.get(urlParameterName);
  const [activeItem, setActiveItem] = useState<TableOfContentsItem>(
    initialUrlParameterId
      ? (items.find((item) => item.urlParameterId === initialUrlParameterId) ?? items[0])
      : items[0],
  );
  const { navigate } = useZuiContext();
  const { breakpoints, width: containerWidth } = useContainerQuery({ containerRef });

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to section from URL on initial load
  useEffect(() => {
    document.getElementById(activeItem.contentDomId)?.scrollIntoView({
      behavior: 'instant',
      block: 'start',
      inline: 'start',
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger when scrollSpy changes
  useEffect(
    debounce(
      () => {
        const updateTableOfContents = async () => {
          if (contentRef.current) {
            const viewportBounds = contentRef.current.getBoundingClientRect();

            const viewPortIsScrolledAtLeastHalfwayIntoContent =
              scrollSpy.scrollY >= scrollSpy.scrollLimitY / 2;
            const viewportFocalPoint = viewPortIsScrolledAtLeastHalfwayIntoContent
              ? {
                  x: viewportBounds.left + viewportBounds.width / 2,
                  y:
                    viewportBounds.top +
                    (scrollSpy.scrollY >= scrollSpy.scrollLimitY - 50
                      ? (viewportBounds.height / 9) * 8
                      : (viewportBounds.height / 6) * 5),
                }
              : {
                  x: viewportBounds.left + viewportBounds.width / 2,
                  y:
                    viewportBounds.top +
                    (scrollSpy.scrollY <= 50
                      ? viewportBounds.height / 9
                      : viewportBounds.height / 6),
                };

            const mostFocalElement = Array.from(
              document.querySelectorAll(`.${contentDomClass}`),
            ).find((contentElement) => {
              const contentElementBounds = contentElement.getBoundingClientRect();
              return (
                viewportFocalPoint.x >= contentElementBounds.left &&
                viewportFocalPoint.x < contentElementBounds.right &&
                viewportFocalPoint.y > contentElementBounds.top &&
                viewportFocalPoint.y < contentElementBounds.bottom
              );
            });

            if (mostFocalElement) {
              const mostFocalItem = items.find((item) => item.contentDomId === mostFocalElement.id);

              if (mostFocalItem && activeItem.urlParameterId !== mostFocalItem.urlParameterId) {
                setActiveItem(mostFocalItem);

                const searchParams = new URLSearchParams(window.location.search);

                if (
                  deepLinkItems &&
                  searchParams.get(urlParameterName) !== mostFocalItem.urlParameterId
                ) {
                  navigate(
                    pathWithParameters(
                      `${window.location.pathname}${window.location.search}`,
                      {
                        [urlParameterName]: mostFocalItem.urlParameterId,
                      },
                      ['builderId'],
                    ),
                  );
                }
              }
            }
          }
        };

        void updateTableOfContents();
      },
      100,
      { leading: true, trailing: true },
    ),
    [items, contentDomClass, scrollSpy.scrollY],
  );

  if (breakpoints.isAtMost[contentBreakpoints.collapseToTabsAtOrBelow] && containerWidth > 0) {
    return <TableOfContentsHorizontal title={title} items={items} activeItem={activeItem} />;
  }

  return <TableOfContentsVertical title={title} items={items} activeItem={activeItem} />;
};

interface TableOfContentsProps extends Pick<TableOfContentsScrollSpyProps, 'title' | 'items'> {
  activeItem: TableOfContentsScrollSpyProps['items'][number];
}

const TableOfContentsVertical = ({ title, items, activeItem }: TableOfContentsProps) => (
  <nav
    aria-label={title}
    data-testid={TABLE_OF_CONTENTS_SCROLL_SPY_VERTICAL_TEST_ID}
    className={tw`scroll-shadows w-[210px] min-w-[210px] max-w-[210px] overflow-y-auto overflow-x-hidden`}
  >
    <ol className={tw`flex flex-col py-5`}>
      {items.map((item, index) => (
        <TableOfContentsEntryVertical
          key={`toc-item-${title}-${item.title}-${index}`}
          {...item}
          active={activeItem.title === item.title}
        />
      ))}
    </ol>
  </nav>
);

const TableOfContentsHorizontal = ({ title, items, activeItem }: TableOfContentsProps) => {
  const telemetry = useTelemetry();

  return (
    <nav
      aria-label={title}
      data-testid={TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_TEST_ID}
      className={tw`flex h-[56px] max-h-[56px] min-h-[56px] w-full max-w-full border-divider-main border-t`}
    >
      <div className={tw`flex h-full items-center bg-white px-2`}>
        <Menu as="div" className={tw`group/menu relative`}>
          <div>
            <MenuButton
              aria-label="Open Table of Contents"
              data-testid={TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_MENU_BUTTON_TEST_ID}
              className={tw`flex items-center justify-center rounded-full bg-transparent p-2 shadow-none hover:bg-content-subtle/25 active:bg-content-subtle/25 group-data-[open]/menu:bg-content-subtle/25`}
            >
              <FontAwesomeIcon className={tw`h-4 w-4 text-content-main opacity-50`} icon={faList} />
            </MenuButton>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            afterEnter={() => telemetry.trackInteraction('toc_scroll_spy_horizontal_menu_open')}
            afterLeave={() => telemetry.trackInteraction('toc_scroll_spy_horizontal_menu_close')}
          >
            <MenuItems
              className={tw`absolute top-6 left-3 z-menu flex w-max origin-top-left flex-col rounded-md bg-white px-1 py-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
              data-testid={TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_MENU_TEST_ID}
            >
              {items.map((item) => (
                <MenuItem key={item.title}>
                  {({ close }) => (
                    <Button
                      type="button"
                      variant="link"
                      className={twx(
                        'flex w-full flex-1 items-center gap-2 rounded-none rounded-r-md border-transparent border-l-2 px-4 py-2 text-left text-content-subtle text-sm transition-all duration-300 hover:bg-content-subtle/10 hover:no-underline',
                        {
                          'border-info-main font-semibold text-content-main':
                            item.urlParameterId === activeItem.urlParameterId,
                          'hover:border-content-subtle/25 hover:text-content-main/85': !(
                            item.urlParameterId === activeItem.urlParameterId
                          ),
                        },
                      )}
                      testId={TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_MENU_ITEM_TEST_ID}
                      telemetryInteractionName="toc_scroll_spy_horizontal_menu_item_click"
                      telemetryContext={{
                        tocItemTitle: item.title,
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        close();

                        document.getElementById(item.contentDomId)?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                          inline: 'start',
                        });

                        event.currentTarget?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                          inline: 'start',
                        });
                      }}
                    >
                      <span>{item.title}</span>
                      {item.selected === true && (
                        <FontAwesomeIcon icon={faCheck} className={tw`h-3 w-3 text-success-main`} />
                      )}
                    </Button>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </Menu>
      </div>
      <ol
        className={tw`no-scrollbar scroll-shadows flex h-full items-center overflow-x-auto overflow-y-hidden`}
      >
        {items.map((item, index) => (
          <TableOfContentsEntryHorizontal
            key={`toc-item-${title}-${item.title}-${index}`}
            {...item}
            active={activeItem.title === item.title}
          />
        ))}
      </ol>
    </nav>
  );
};

interface TableOfContentsEntryProps extends TableOfContentsItem {
  active: boolean;
}

const TableOfContentsEntryVertical = ({
  title,
  contentDomId,
  active,
  selected,
}: TableOfContentsEntryProps) => {
  const telemetry = useTelemetry();
  const entryRef = useRef<HTMLAnchorElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(() => {
    if (active) {
      entryRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [active, entryRef.current]);

  return (
    <li>
      <a
        ref={entryRef}
        data-testid={TABLE_OF_CONTENTS_SCROLL_SPY_VERTICAL_ITEM_TEST_ID}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          telemetry.trackInteraction('toc_scroll_spy_vertical_item_click', {
            tocItemTitle: title,
          });

          document.getElementById(contentDomId)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
          });

          event.currentTarget?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
          });
        }}
        href={`#${contentDomId}`}
        className={twx(
          'flex h-full w-full items-center justify-between gap-1 border-transparent border-l-2 px-4 py-2 text-content-subtle transition-all duration-100',
          {
            'border-info-main font-semibold text-content-main': active,
            'hover:border-content-subtle/25 hover:text-content-main/85': !active,
          },
        )}
      >
        <span>{title}</span>
        {selected === true && (
          <FontAwesomeIcon icon={faCheck} className={tw`h-3 w-3 text-success-main`} />
        )}
      </a>
    </li>
  );
};

const TableOfContentsEntryHorizontal = ({
  title,
  contentDomId,
  active,
  selected,
}: TableOfContentsEntryProps) => {
  const telemetry = useTelemetry();
  const entryRef = useRef<HTMLAnchorElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(() => {
    if (active) {
      entryRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [active, entryRef.current]);

  return (
    <li className={tw`h-full w-fit max-w-fit`}>
      <a
        ref={entryRef}
        data-testid={TABLE_OF_CONTENTS_SCROLL_SPY_HORIZONTAL_ITEM_TEST_ID}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          telemetry.trackInteraction('toc_scroll_spy_horizontal_item_click', {
            tocItemTitle: title,
          });

          document.getElementById(contentDomId)?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });

          event.currentTarget?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'start',
          });
        }}
        href={`#${contentDomId}`}
        className={twx(
          'flex h-full w-full items-center justify-between gap-1 whitespace-nowrap border-transparent border-b-2 px-4 py-2 text-content-subtle text-sm transition-all duration-100',
          {
            'border-info-main font-semibold text-content-main': active,
            'hover:border-content-subtle/25 hover:text-content-main/85': !active,
          },
        )}
      >
        <div className={tw`block`}>{title}</div>
        {selected === true && (
          <FontAwesomeIcon icon={faCheck} className={tw`h-3 w-3 text-success-main`} />
        )}
      </a>
    </li>
  );
};
